import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, FileText, Handshake } from "lucide-react";
import { formatApiError } from "../../contexts/AuthContext";
import { useLang, fmtMoney } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

export const DealRoomPanel = ({ pitchId, onClose }) => {
  const lang = useLang();
  const [data, setData] = useState(null);
  const [docName, setDocName] = useState("");
  const [file, setFile] = useState(null);
  const [term, setTerm] = useState({ amount: "", equity: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/dealroom/${pitchId}`, headers()).then((r) => setData(r.data)).catch(() => setData(null));
  };
  useEffect(load, [pitchId]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/dealroom/${pitchId}/documents?name=${encodeURIComponent(docName || file.name)}`, form, headers());
      toast.success("Document uploaded");
      setFile(null); setDocName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const propose = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/dealroom/${pitchId}/terms`, { ...term, amount: parseInt(term.amount, 10) || 0 }, headers());
      toast.success("Term sheet drafted");
      setTerm({ amount: "", equity: "", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const send = async (termId) => {
    try {
      await axios.post(`${API}/dealroom/${pitchId}/terms/${termId}/send`, {}, headers());
      toast.success("Term sheet sent to the founder");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const respond = async (termId, action) => {
    try {
      await axios.post(`${API}/dealroom/${pitchId}/terms/${termId}/respond?action=${action}`, {}, headers());
      toast.success(action === "accepted" ? "Deal accepted 🤝" : "Terms declined");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (data === null) return <LoaderCircle className="mt-6 h-5 w-5 animate-spin text-[#F5B300]" />;
  const { room, documents, terms } = data;
  return (
    <div className="space-y-4 rounded-3xl border border-[#F5B300]/25 bg-[#09090B]/80 p-6 backdrop-blur-xl" data-testid="deal-room">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 font-display text-lg font-bold"><Handshake className="h-5 w-5 text-[#F5B300]" /> Deal Room — {room.title}</p>
        <button data-testid="deal-close" onClick={onClose} className="rounded-full border border-white/10 px-3 py-1 text-xs text-slate-400 hover:text-white">✕</button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <section>
          <p className="font-display text-sm font-bold">Documents ({documents.length})</p>
          <div className="mt-2 space-y-2">
            {documents.map((d) => (
              <a key={d.id} href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noreferrer" data-testid={`doc-${d.id.slice(-6)}`}
                className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#09090B]/80 px-3.5 py-2.5 text-xs text-slate-300 hover:text-white">
                <FileText className="h-3.5 w-3.5" /> {d.name}
              </a>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input data-testid="doc-name" value={docName} onChange={(e) => setDocName(e.target.value)} placeholder="Document name"
              className="flex-1 rounded-2xl border border-white/10 bg-[#09090B]/80 px-3.5 py-2.5 text-xs text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
            <label className="cursor-pointer rounded-2xl border border-dashed border-white/20 px-3.5 py-2.5 text-xs text-slate-400 hover:border-[#06B6D4]/60 hover:text-white">
              {file ? file.name : "Choose file"}
              <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <button data-testid="doc-upload" onClick={upload} disabled={busy || !file}
              className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-4 py-2 font-display text-[10px] font-bold uppercase text-white disabled:opacity-50">
              {busy ? <LoaderCircle className="h-3 w-3 animate-spin" /> : "Upload"}
            </button>
          </div>
        </section>

        <section>
          <p className="font-display text-sm font-bold">Term Sheets ({terms.length})</p>
          <div className="mt-2 space-y-2">
            {terms.map((t) => (
              <div key={t.id} data-testid={`term-${t.id.slice(-6)}`} className="rounded-xl border border-white/10 bg-[#09090B]/80 p-3.5">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold">{fmtMoney(t.amount, "INR", "₹")} for {t.equity}</p>
                  <span className={`rounded-full px-2 py-0.5 font-jbmono text-[9px] font-bold uppercase ${
                    t.status === "accepted" ? "bg-[#22C55E]/10 text-[#22C55E]" : t.status === "rejected" ? "bg-[#EF4444]/10 text-[#EF4444]" : "bg-[#F97316]/10 text-[#F97316]"
                  }`}>{t.status}</span>
                </div>
                {t.note && <p className="mt-1 text-[11px] text-slate-400">{t.note}</p>}
                <div className="mt-2 flex gap-1.5">
                  {t.status === "draft" && (
                    <button data-testid={`term-send-${t.id.slice(-6)}`} onClick={() => send(t.id)} className="rounded-full bg-gradient-to-r from-[#F5B300] to-[#F97316] px-3 py-1 font-display text-[9px] font-bold uppercase text-[#09090B]">
                      Send to founder
                    </button>
                  )}
                  {t.status === "sent" && (
                    <>
                      <button data-testid={`term-accept-${t.id.slice(-6)}`} onClick={() => respond(t.id, "accepted")} className="rounded-full bg-[#22C55E]/10 px-3 py-1 font-jbmono text-[9px] font-bold text-[#22C55E]">
                        Accept
                      </button>
                      <button data-testid={`term-reject-${t.id.slice(-6)}`} onClick={() => respond(t.id, "rejected")} className="rounded-full border border-[#EF4444]/40 px-3 py-1 text-[9px] font-bold text-[#EF4444]">
                        Decline
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-2 border-t border-white/10 pt-3">
            <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-500">Draft term sheet</p>
            <div className="flex gap-2">
              <input data-testid="term-amount" value={term.amount} onChange={(e) => setTerm({ ...term, amount: e.target.value })} placeholder="Amount (₹)"
                className="flex-1 rounded-2xl border border-white/10 bg-[#09090B]/80 px-3.5 py-2.5 text-xs text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
              <input data-testid="term-equity" value={term.equity} onChange={(e) => setTerm({ ...term, equity: e.target.value })} placeholder="Equity %"
                className="w-24 rounded-2xl border border-white/10 bg-[#09090B]/80 px-3.5 py-2.5 text-xs text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
            </div>
            <button data-testid="term-propose" onClick={propose} disabled={busy || !term.amount}
              className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-4 py-2 font-display text-[10px] font-bold uppercase text-white disabled:opacity-50">
              {busy ? <LoaderCircle className="h-3 w-3 animate-spin" /> : "Draft terms"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};
